import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot} from '@angular/router';
import {Observable, of} from 'rxjs';
import {CompanyOptInModalService} from '../company/services/company-opt-in-modal.service';
import {CompanyOptInService} from '../company/services/company-opt-in.service';
import {CompanyService} from '../company/services/company.service';
import {OurUserService} from '../users/services/user.service';
import {OptInModalInfo} from '../message-models/optin-modal-info.class';
import {CompanyOptInAcceptanceSummary} from '../domain-model/company-opt-in-acceptance-summary.class';
import {switchMap} from 'rxjs/operators';
import {CompanyInfo} from '../domain-model/company-info.class';
import {ICompanyInfo} from '../message-models/company-info.interface';
import {IUserInfo} from '../users/models/userInfo.interface';

@Injectable({
  providedIn: 'root'
})
export class CompanyOptInGuard implements CanActivate {
  companyOptInAcceptanceSummaries: CompanyOptInAcceptanceSummary[] = [];

  constructor(private companyOptInModalService: CompanyOptInModalService, private companyService: CompanyService, private userService: OurUserService,
              private companyOptInService: CompanyOptInService) {
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return this.userService.getLoggedInUser().pipe(switchMap(user => {
      const loggedInUser = user;
      const companyId = loggedInUser?.companyId;
      if (!loggedInUser?.isInternalAdminUser) {
        return this.companyService.getCompany(companyId).pipe(switchMap(comp => {
          if (comp.anyOptInsNotAccepted) {
            return this.companyOptInService.getOptInModalDetails().pipe(switchMap(modalData => {
              const optInModalInfo = new OptInModalInfo(modalData);
              return this.companyOptInService.getPendingCompanyOptInAcceptances(companyId).pipe(switchMap(
                result => {
                  const optInAcceptanceSummaries = result;
                  this.companyOptInAcceptanceSummaries = new Array<CompanyOptInAcceptanceSummary>();
                  optInAcceptanceSummaries?.forEach(summary => {
                    this.companyOptInAcceptanceSummaries?.push(new CompanyOptInAcceptanceSummary(summary));
                  });
                  this.companyOptInAcceptanceSummaries.sort((a, b) => this.compareHtmlStrings(a.optInDescription, b.optInDescription));
                  return this.companyOptInModalService.openCompanyOptInModal(optInModalInfo, this.companyOptInAcceptanceSummaries)
                    .pipe(switchMap(optInModalRef => {
                      optInModalRef.content.recordOptInActionsClicked.subscribe(results => {
                        if (!comp.contractAgreed) {
                          return this.openContractAgreementModal(comp, loggedInUser);
                        } else {
                          return of(true);
                        }
                      });
                      return of(true);
                    }));
                }
              ));
            }));
          } else {
            if (!comp.contractAgreed) {
              return this.openContractAgreementModal(comp, loggedInUser);
            }

            return of(true);
          }
        }));
      } else {
        return of(true);
      }
    }));
  }

  private openContractAgreementModal(comp: ICompanyInfo, loggedInUser: IUserInfo): Observable<boolean> {
    const optInModalInfo = new OptInModalInfo();
    optInModalInfo.continueButtonText = 'I ' + loggedInUser.displayName + ' Agree to the Terms and Conditions Above';
    optInModalInfo.optInModalText = '';
    optInModalInfo.optInModalTitle = 'eCOMS Acceptance';
    const company = new CompanyInfo(comp);
    return this.companyOptInModalService.openCompanyContractModal(optInModalInfo, company).pipe(switchMap(optInModalRef => {
      return of(true);
    }));
  }


  compareHtmlStrings(str1: string, str2: string) {
    const div1 = document.createElement('div');
    div1.innerHTML = str1;
    const div2 = document.createElement('div');
    div2.innerHTML = str2;
    if (div1.textContent.toLocaleUpperCase() > div2.textContent.toLocaleUpperCase()) {
      return 1;
    } else if (div1.textContent.toLocaleUpperCase() < div2.textContent.toLocaleUpperCase()) {
      return -1;
    }
    return 0;
  }
}
